import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  collection,
  getDocs,
  limit,
  query,
  where,
  orderBy,
  startAfter,
  getDoc,
  doc,
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import { toast } from "@/components/ui/use-toast";

export const fetchCategories = createAsyncThunk(
  "categories/fetchCategories",
  async (
    { lastVisible = null, isLoadMore = false } = {},
    { rejectWithValue },
  ) => {
    try {
      const LIMIT = 10;
      let q = query(
        collection(db, "partCategories"),
        orderBy("totalParts", "desc"),
        limit(LIMIT),
      );

      if (lastVisible) {
        q = query(
          collection(db, "partCategories"),
          orderBy("totalParts", "desc"),
          startAfter(lastVisible),
          limit(LIMIT),
        );
      }

      const snapshot = await getDocs(q);

      const categories = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      const newLastVisible =
        snapshot.docs.length > 0
          ? snapshot.docs[snapshot.docs.length - 1]
          : null;

      return {
        categories,
        lastVisible: newLastVisible,
        hasMore: snapshot.docs.length === LIMIT,
        isLoadMore,
      };
    } catch (error) {
      console.error("Fetching Error", error.message);
      toast({
        variant: "destructive",
        title: "Fetching Error",
        description: "Could not fetch Categories due to a database error.",
      });
      return rejectWithValue(error.message);
    }
  },
);

export const fetchPartsByCategories = createAsyncThunk(
  "categories/fetchPartsByCategories",
  async (categories, { rejectWithValue }) => {
    try {
      const promises = categories?.map(async ({ id, name }) => {
        const q = query(
          collection(db, "parts"),
          where("categoryId", "==", id),
          orderBy("sortOrder", "asc"),
          orderBy("dateAdded", "desc"),
          limit(4),
        );

        const snapshot = await getDocs(q);
        const parts = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        return { name, parts };
      });

      const results = await Promise.all(promises);

      const formatted = results.reduce((acc, { name, parts }) => {
        acc[name] = parts;
        return acc;
      }, {});

      return formatted;
    } catch (error) {
      console.error("Fetching Error:", error.message);
      return rejectWithValue(error.message);
    }
  },
);

export const fetchPartsBySingleCategory = createAsyncThunk(
  "categories/fetchPartsBySingleCategory",
  async (
    { categoryId, lastVisible = null, isLoadMore = false },
    { rejectWithValue },
  ) => {
    try {
      const partsRef = collection(db, "parts");
      const LIMIT = 10;

      let q = query(
        partsRef,
        where("categoryId", "==", categoryId),
        orderBy("sortOrder", "asc"),
        limit(LIMIT),
      );

      if (lastVisible) {
        q = query(
          partsRef,
          where("categoryId", "==", categoryId),
          orderBy("sortOrder", "asc"),
          startAfter(lastVisible),
          limit(LIMIT),
        );
      }

      const snapshot = await getDocs(q);

      const parts = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      const newLastVisible =
        snapshot.docs.length > 0
          ? snapshot.docs[snapshot.docs.length - 1]
          : null;

      const hasMore = snapshot.docs.length === LIMIT;

      return { parts, lastVisible: newLastVisible, hasMore, isLoadMore };
    } catch (error) {
      console.error("Fetching Error", error.message);

      toast({
        variant: "destructive",
        title: "Fetching Error",
        description: "Could not fetch parts due to a database error.",
      });

      return rejectWithValue(error.message);
    }
  },
);

export const fetchAllCategories = createAsyncThunk(
  "categories/fetchAllCategories",
  async (_, { rejectWithValue }) => {
    try {
      const q = query(
        collection(db, "partCategories"),
        orderBy("totalParts", "desc"),
      );

      const snapshot = await getDocs(q);

      const categories = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      return categories;
    } catch (error) {
      console.error("Fetching Error", error.message);
      toast({
        variant: "destructive",
        title: "Fetching Error",
        description: "Could not fetch Categories due to a database error.",
      });
      return rejectWithValue(error.message);
    }
  },
);

export const filterCategories = createAsyncThunk(
  "categories/filterCategories",
  async (id, { rejectWithValue }) => {
    try {
      if (!id && id !== "all") {
        toast({
          variant: "destructive",
          title: "Category ID is required",
          description: "Please try again to filter categories",
        });
        return rejectWithValue("Category ID is required");
      }

      const docRef = doc(db, "partCategories", id);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error("Category not found");
      }

      const categoryData = [{ id: docSnap.id, ...docSnap.data() }];

      const q = query(
        collection(db, "parts"),
        where("categoryId", "==", id),
        orderBy("sortOrder", "asc"),
        limit(4),
      );

      const snapshot = await getDocs(q);
      const parts = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      return { category: categoryData, parts };
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error filtering categories",
        description:
          "There was an error while filtering categories. Please try again.",
      });
      return rejectWithValue(error.message);
    }
  },
);

export const fetchLandingPageCategories = createAsyncThunk(
  "categories/fetchLandingPageCategories",
  async (_, { rejectWithValue }) => {
    try {
      const partCategoriesCollection = collection(db, "partCategories");

      const q = query(
        partCategoriesCollection,
        orderBy("totalParts", "desc"),
        limit(6),
      );
      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
    } catch (error) {
      console.error("Error fetching categories:", error);
      toast({
        variant: "destructive",
        title: "Fetching Error",
        description:
          error.message ||
          "Could not fetch categories due to a database error.",
      });
      return rejectWithValue(
        error.message || "Could not fetch categories due to a database error.",
      );
    }
  },
);
